import { useEffect } from "react";

const audioContext = AudioContext ? new AudioContext() : null;

function rootMeanSquare(samples) {
  const sumSq = samples.reduce((sumSq, sample) => sumSq + sample * sample, 0);
  return Math.sqrt(sumSq / samples.length);
}

class AudioTrackMonitor {
  analyser?: AnalyserNode;
  source?: MediaStreamAudioSourceNode;
  interval?: NodeJS.Timeout;

  disconnectRequested = false;

  connect = async (
    mediaStreamTrack: MediaStreamTrack,
    onLevelChanged: (level: number) => void,
  ) => {
    if (!audioContext) {
      return;
    }

    await audioContext.resume();
    if (this.disconnectRequested) {
      this.disconnect();
      return;
    }

    // Create an analyser to access the raw audio samples from the microphone.
    this.analyser = audioContext.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0.5;

    // Connect the LocalAudioTrack's media source to the analyser.
    const stream = new MediaStream([mediaStreamTrack]);
    this.source = audioContext.createMediaStreamSource(stream);
    this.source.connect(this.analyser);

    const samples = new Uint8Array(this.analyser.frequencyBinCount);
    let level: number | null = null;

    // Periodically calculate the audio level from the captured samples,
    // and if changed, call the callback with the new audio level.
    this.interval = setInterval(() => {
      if (this.disconnectRequested) {
        this.disconnect();
        return;
      }

      this.analyser?.getByteFrequencyData(samples);
      const rms = rootMeanSquare(samples);
      const log2Rms = rms && Math.log2(rms);

      // Audio level ranges from 0 (silence) to 10 (loudest).
      const newLevel = Math.ceil((10 * log2Rms) / 8);
      if (level !== newLevel) {
        level = newLevel;
        onLevelChanged(level);
      }

      // Continue calculating the level only if the audio track is live.
      if (mediaStreamTrack.readyState !== "live") {
        this.disconnect();
      }
    }, 200);
  };

  disconnect = () => {
    this.disconnectRequested = true;
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.analyser?.disconnect();
    this.source?.disconnect();
  };
}

const useAudioTrackMonitor = (
  mediaStreamTrack: MediaStreamTrack | undefined,
  onLevelChanged: (level: number) => void,
) => {
  useEffect(() => {
    if (!mediaStreamTrack) {
      return;
    }
    const monitor = new AudioTrackMonitor();
    monitor.connect(mediaStreamTrack, onLevelChanged);
    return () => monitor.disconnect();
  }, [mediaStreamTrack, onLevelChanged]);
};

export default useAudioTrackMonitor;
